import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// ── Sharp-cornered button that can render as Link, anchor or button ──
export const Btn = ({ variant = 'primary', to, href, compact, className = '', children, ...rest }) => {
  const cls = `btn btn-${variant}${compact ? ' btn-compact' : ''} ${className}`;
  if (to) return <Link to={to} className={cls} {...rest}>{children}</Link>;
  if (href) {
    const ext = /^https?:/i.test(href);
    return <a href={href} className={cls} {...(ext ? { target: '_blank', rel: 'noreferrer' } : {})} {...rest}>{children}</a>;
  }
  return <button className={cls} {...rest}>{children}</button>;
};

export const Eyebrow = ({ children, color, className = '', style }) => (
  <span
    className={`eyebrow ${className}`}
    style={{ color: color || '#B4882F', ...style }}
  >
    {children}
  </span>
);

export const Divider = ({ className = '', color }) => (
  <div className={`divider ${className}`} style={{ background: color || 'linear-gradient(90deg, rgba(180,136,47,0) 0%, rgba(180,136,47,0.5) 50%, rgba(180,136,47,0) 100%)' }} />
);

// Standard section with generous breathing room
export const Section = ({ children, className = '', id, style, testId }) => (
  <section
    id={id}
    data-testid={testId}
    className={`py-20 md:py-[120px] ${className}`}
    style={style}
  >
    <div className="mx-auto w-full max-w-[1280px] px-6 md:px-12">{children}</div>
  </section>
);

// Trust numbers strip — four numbers inline, no cards
export const StatStrip = ({ stats, dark }) => (
  <div className="grid grid-cols-2 md:grid-cols-4">
    {stats.map((s, i) => (
      <div
        key={s.label}
        className={`px-4 py-6 md:py-0 text-center ${i > 0 ? 'md:border-l' : ''} border-t md:border-t-0`}
        style={{ borderColor: dark ? 'rgba(249, 250, 248,0.18)' : '#E6DFD4' }}
      >
        <div
          className="font-display leading-none"
          style={{ fontSize: 'clamp(40px, 5vw, 56px)', color: dark ? '#F2F0ED' : '#1B1815', fontWeight: 700 }}
        >
          {s.number}
        </div>
        <div
          className="mt-3 font-body uppercase"
          style={{ fontSize: '15px', letterSpacing: '0.2em', fontWeight: 600, color: dark ? '#F2F0ED' : '#1B1815' }}
        >
          {s.label}
        </div>
        <div className="mt-1 font-body uppercase" style={{ fontSize: '15px', letterSpacing: '0.14em', color: dark ? 'rgba(249, 250, 248,0.55)' : '#A79E95' }}>
          {s.sub}
        </div>
      </div>
    ))}
  </div>
);

// The one dark forest-green CTA moment per page
export const ForestCTA = ({ label, heading, body, children, testId }) => (
  <section data-testid={testId} className="w-full" style={{ background: '#1B1815' }}>
    <div className="mx-auto w-full max-w-[900px] px-6 md:px-12 py-20 md:py-[120px] text-center">
      {label && (
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <Eyebrow color="rgba(249, 250, 248,0.5)">{label}</Eyebrow>
        </motion.div>
      )}
      <motion.h2
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.05 }}
        className="mt-6 font-display"
        style={{ color: '#F2F0ED', fontSize: 'clamp(36px, 5vw, 60px)', lineHeight: 1.05 }}
      >
        {heading}
      </motion.h2>
      {body && (
        <p className="mx-auto mt-6 font-body" style={{ color: 'rgba(249, 250, 248,0.65)', maxWidth: 520, fontSize: '18px', lineHeight: 1.7 }}>
          {body}
        </p>
      )}
      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
        {children}
      </div>
    </div>
  </section>
);

// Warm full-bleed / column photo
export const Photo = ({ src, alt, className = '', style }) => (
  <img
    src={src}
    alt={alt}
    loading="lazy"
    className={`warm-photo object-cover ${className}`}
    style={style}
  />
);

// Reveal-on-scroll helper
export const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);
